(() => {
  'use strict';
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection;
  const records = new Map();
  let active = null, suspended = false, generation = 0, sdkPromise;
  const previewDuration = 15000;

  function youtubeReady() {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (sdkPromise) return sdkPromise;
    sdkPromise = new Promise((resolve, reject) => {
      let finished = false;
      const previous = window.onYouTubeIframeAPIReady;
      const finish = () => {
        if (finished || !window.YT?.Player) return;
        finished = true;
        clearTimeout(timeout);
        document.removeEventListener('youTubeReady', finish);
        resolve(window.YT);
      };
      const timeout = setTimeout(() => {
        if (finished) return;
        finished = true;
        document.removeEventListener('youTubeReady', finish);
        reject(new Error('YouTube preview unavailable'));
      }, 12000);
      window.onYouTubeIframeAPIReady = function () {
        try { previous?.(); } finally { finish(); }
      };
      document.addEventListener('youTubeReady', finish);
      // Share Impulse's loader so product videos and previews use one SDK.
      if (window.theme?.LibraryLoader) {
        window.theme.LibraryLoader.load('youtubeSdk');
      } else if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.id = 'youtube-sdk';script.src = 'https://www.youtube.com/iframe_api';script.async = true;
        document.head.append(script);
      }
      finish();
    });
    return sdkPromise;
  }

  function eligible(record) {
    const bounds = record.shell.getBoundingClientRect();
    return record.shell.isConnected && record.visible > .5 && bounds.width >= 200 && bounds.height >= 200 &&
      document.activeElement !== record.thumbnail &&
      !record.blocked && (!record.suppressAuto || record === active) &&
      !document.hidden && !suspended && !motion.matches && !connection?.saveData;
  }

  function stopActive() {
    generation++;
    const record = active;
    active = null;
    if (!record) return;
    clearTimeout(record.timer);clearTimeout(record.stall);
    record.player?.destroy?.();
    // Removing the iframe before starting its replacement prevents overlapping playback.
    record.embed.replaceChildren();record.embed.hidden = true;record.thumbnail.hidden = false;
    record.player = null;record.frame = null;record.manual = false;
  }

  function candidates() { return [...records.values()].filter(eligible); }
  function update() {
    if (active && eligible(active)) return;
    stopActive();
    const next = candidates()[0];
    if (next) activate(next);
  }
  function advance(record) {
    if (active !== record || !eligible(record)) { update(); return; }
    if (document.activeElement === record.frame || record.player?.isMuted?.() === false) record.manual = true;
    if (record.manual) return;
    const list = candidates();
    const next = list[(list.indexOf(record) + 1) % list.length];
    if (next && next !== record) activate(next);
    else record.timer = setTimeout(() => advance(record), previewDuration);
  }
  function fail(record) {
    record.blocked = true;
    if (active === record) { stopActive();update(); }
  }

  async function activate(record) {
    stopActive();active = record;
    const token = generation;
    try {
      const YT = await youtubeReady();
      if (active !== record || token !== generation) return;
      if (!eligible(record)) { stopActive();update();return; }
      const frame = document.createElement('iframe');
      const params = new URLSearchParams({enablejsapi:'1',autoplay:'0',playsinline:'1',controls:'1',rel:'0',origin:location.origin});
      frame.src = `https://www.youtube-nocookie.com/embed/${record.id}?${params}`;
      frame.title = `${record.title} — muted preview`;
      frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';frame.allowFullscreen = true;
      record.frame = frame;record.embed.replaceChildren(frame);record.embed.hidden = false;record.thumbnail.hidden = true;
      record.stall = setTimeout(() => fail(record), 12000);
      record.player = new YT.Player(frame, {events:{
        onReady(event) {
          if (active !== record || token !== generation) { event.target.destroy();return; }
          if (!eligible(record)) { stopActive();update();return; }
          event.target.mute();event.target.playVideo();
        },
        onStateChange(event) {
          if (active !== record || token !== generation) return;
          if (event.data === 1) {
            if (!eligible(record)) { stopActive();return; }
            clearTimeout(record.stall);clearTimeout(record.timer);
            record.suppressAuto = false;
            if (event.target.isMuted() === false || document.activeElement === record.frame) record.manual = true;
            if (!record.manual) record.timer = setTimeout(() => advance(record), previewDuration);
          } else if (event.data === 2) {
            // A viewer pausing the native player takes precedence over preview rotation.
            record.manual = true;record.suppressAuto = true;clearTimeout(record.timer);clearTimeout(record.stall);
          } else if (event.data === 0) {
            clearTimeout(record.timer);
            if (!record.manual) advance(record);
          }
        },
        onAutoplayBlocked() { if (token === generation) fail(record); },
        onError() { if (token === generation) fail(record); }
      }});
    } catch (_) {
      if (active === record && token === generation) fail(record);
    }
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const record = records.get(entry.target);
      if (record) record.visible = entry.isIntersecting ? entry.intersectionRatio : 0;
    });
    update();
  }, {threshold:[0,.5,.6,1]}) : null;

  function init(root = document) {
    if (!observer) return;
    root.querySelectorAll('[data-aa-preview]').forEach(shell => {
      if (records.has(shell) || !/^[\w-]{11}$/.test(shell.dataset.aaPreview)) return;
      const thumbnail = shell.querySelector('a[data-aa-video]'),embed = shell.querySelector('.aa-preview-embed');
      if (!thumbnail || !embed) return;
      records.set(shell,{shell,thumbnail,embed,id:shell.dataset.aaPreview,title:shell.dataset.aaPreviewTitle,visible:0});
      observer.observe(shell);
    });
  }
  function suspend() { suspended = true;stopActive(); }
  function resume() { suspended = false;update(); }
  document.addEventListener('aa:video-open',suspend);
  document.addEventListener('aa:video-close',resume);
  document.addEventListener('modalOpen.VideoModal',suspend);
  document.addEventListener('modalClose.VideoModal',resume);
  document.addEventListener('visibilitychange',update);
  document.addEventListener('focusin',update);
  document.addEventListener('focusout',update);
  window.addEventListener('resize',update);
  window.addEventListener('pagehide',stopActive);
  window.addEventListener('pageshow',update);
  motion.addEventListener('change',update);
  connection?.addEventListener?.('change',update);
  document.addEventListener('shopify:section:load',event => init(event.target));
  document.addEventListener('shopify:section:unload',event => {
    for (const [shell,record] of records) {
      if (!event.target.contains(shell)) continue;
      if (active === record) stopActive();
      observer?.unobserve(shell);records.delete(shell);
    }
    update();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => init());else init();
})();
